import { useMemo } from "react";
import { gql, useQuery } from "@apollo/client";
import { useApolloClients } from "~/components/ApolloClients";

const HIRING_TEAM_QUERY = gql`
  query HiringTeamQuery($teamId: ID!) {
    team(teamId: $teamId) {
      interviewers {
        id
        firstName
        lastName
        email
      }
    }
  }
`;

interface RawTeamMember {
  id: string;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
}

export interface HiringTeamMember {
  id: string;
  name: string;
  email?: string;
}

/**
 * The listing's hiring team — the people who can be put on an interview panel.
 *
 * Same source the legacy scheduling wizard uses (`team.interviewers`), so the
 * external-interview flow offers exactly the same set of people as scheduling
 * one. Deliberately NOT `useOrgHireUsers`, which is the org-wide list of people
 * who can be asked to review a *guide* — a different question entirely.
 */
export function useHiringTeam(teamId?: string) {
  const { trackyrClient } = useApolloClients();

  const { data, loading } = useQuery(HIRING_TEAM_QUERY, {
    client: trackyrClient,
    variables: { teamId },
    skip: !teamId,
    fetchPolicy: "cache-and-network",
  });

  const members: HiringTeamMember[] = useMemo(
    () =>
      ((data?.team?.interviewers ?? []) as RawTeamMember[]).map((member) => ({
        id: member.id,
        name:
          [member.firstName, member.lastName]
            .filter(Boolean)
            .join(" ")
            .trim() ||
          member.email ||
          "Unknown",
        email: member.email ?? undefined,
      })),
    [data],
  );

  return { members, loading };
}
